import * as moment from 'moment';
import * as angular from 'angular';
import * as _ from 'lodash';

import {
  Input,
  Inject,
  OnInit,
  Output,
  Component,
  EventEmitter,
} from 'ng-metadata/core';
import * as $ from 'jquery';

import { CartService } from 'services/cart.service';
import { ProductService } from 'services/product.service';
import { EtomsService } from 'services/etoms.service';

import productOptionsHtml from './product-options.html';
import './product-options.scss';
import { parseDomain } from 'helpers/parseDomain';

/**
 * Product Options Component
 *
 * Displayed to configure a product under a traveler
 */
@Component({
  selector: 'gwProductOptions',
  template: productOptionsHtml,
})
export class ProductOptionsComponent implements OnInit {
  public cart = (<any>window).GW.cart;
  public productData: any;

  public state = {
    controls: false,
    confirming: false,
    entriesOptions: [],
    outboundOptions: [],
    processingOptions: [],
    productDetails: null,
    entryOptionLabels: null,
    validityOptions: [],
  };

  public model = {
    entries: null,
    validity: null,
    processing: null,
    outbound: null,
    embassy_fee: null,
  };

  public etomsEnabled: boolean;

  public embassyFeeAddon;

  @Input() index;
  @Input() domain;
  @Input() product;
  @Input() traveler;
  @Input() deletable: boolean;
  @Output() onConfirm = new EventEmitter<{}>();
  @Output() onDelete = new EventEmitter<{}>();

  constructor(
    @Inject('$filter') private $filter,
    @Inject('$rootScope') private $rootScope: ng.IRootScopeService,
    @Inject(CartService) private cartService: CartService,
    @Inject(ProductService) private productService: ProductService,
    @Inject(EtomsService) private etomsService: EtomsService,
  ) {}

  ngOnInit() {
    // Get entry types labels
    this.state.entryOptionLabels = this.cartService.entryOptionLabels;

    // If the provided traveler already has a name, assume it's been confirmed
    // Or if it's the first unconfirmed traveler
    this.confirmTraveler();

    this.productService
      .getOutboundOptions()
      .then((options) => {
        this.state.outboundOptions = options;

        if (!this.traveler.outbound) {
          this.traveler.outbound = this.state.outboundOptions[0];
        }
      });

    this.etomsEnabled = this.etomsService.enabled();
  }

  async confirmTraveler() {
    this.state.confirming = true;
    this.traveler.confirmed = false;

    try {
      const response: any = await this.productService
        .confirmProductRequirement(
          this.product.uuid,
          this.traveler.citizenship,
          this.traveler.residency,
        );

      this.state.productDetails = response.data;
    } catch (e) {
      throw e;
    } finally {
      this.state.confirming = false;
    }

    // Traveler is confirmed
    this.traveler.confirmed = true;

    // Set requirement status
    this.traveler.required = this.state.productDetails.required;

    if (!this.traveler.required) {
      return;
    }

    // Prepare entries select options, select first by default
    this.state.entriesOptions = Object.keys(this.state.productDetails.options);
    this.model.entries = this.state.entriesOptions[0];

    // Prepare processing select options, select first by default
    this.setupProcessingOptions();

    // initialize with the mandatory addons
    this.traveler.addons = angular.copy(this.product.addons.selected);
  }

  setupProcessingOptions(resetValidity: Boolean = true) {
    let options;
    if (this.etomsEnabled) {
      const validity = Object.keys(this.state.productDetails.options[this.model.entries]);
      this.state.validityOptions[this.model.entries] = validity;
      if (resetValidity) {
        this.model.validity = validity[0];
      }
      options = this.state.productDetails.options[this.model.entries][this.model.validity];
    } else {
      options = this.state.productDetails.options[this.model.entries];
    }

    this.state.processingOptions = options
      .map(this.cartService.mapVisaProcessingOptions.bind(this));

    // Add first set of product options to traveler
    this.traveler.options = this.state.processingOptions[0];
  }

  showSubTotal() {
    let subtotal = this.traveler.options.processingFee;

    this.traveler.addons.forEach((select) => {
      const addOn = this.product.addons.list[select];
      subtotal += parseInt(addOn.value, 10);

      if (select === this.product.addons.embassy_fee) {
        subtotal += this.traveler.options.embassyFee;
      }
    });

    return this.$filter('currency')(subtotal);
  }

  /**
   * Emit rootScope event to refresh requirements list with new jurisdiction settings
   */
  refreshRequirements(type, value) {
    if (typeof type === 'undefined' || typeof value === 'undefined') {
      return;
    }

    this.traveler[type] = value;

    const data = {
      type: 'visa',
      id: this.traveler.id,
      subtype: this.product.subtype,
      country: this.product.country,
      residency: this.traveler.residency,
      citizenship: this.traveler.citizenship,
    };

    this.$rootScope.$broadcast('gw-refresh-requirements', data);
    this.confirmTraveler();
  }

  /**
   * When a md-select-backdrop exist the page can't be scrolled
   * but the default angular scroll blocker is not working
   *
   * @param show
   */
  onShow(show: boolean) {
    $('html, body')
      .css('overflow', show ? 'hidden' : 'auto');
  }

  /**
   * Toggle the addon selection
   *
   * @param key
   */
  toggleAddonSelection(key) {
    if (this.isAddonSelected(key)) {
      this.traveler.addons = this.traveler.addons.filter(addon => addon !== key);
    } else {
      this.traveler.addons.push(key);
    }

    if (key === this.product.addons.embassy_fee) {
      this.traveler.options.chargeEmbassyFee = !this.traveler.options.chargeEmbassyFee;
    }
  }

  /**
   * Check if the addOn is already selected
   *
   * @param key
   */
  isAddonSelected(key) {
    return this.traveler.addons.find(addOn => addOn === key) !== undefined;
  }

  /**
   * To know how many add-ons are enabled
   */
  addonsCount(): Number {
    let count = Object.keys(this.product.addons.list).length;
    if (this.traveler.options.embassyFee === 0 &&
      this.product.addons.list.find(addon => addon.label === 'Embassy/Consulate Fee') !== -1) {
      count = count - 1;
    }

    return count;
  }

  /**
   * Should we show this addon
   * @param label
   */
  showAddon(label: String): Boolean {
    if (label !== 'Embassy/Consulate Fee') {
      return true;
    }

    return this.traveler.options.embassyFee !== 0;
  }

  /**
   * Return the visa type label
   */
  getTypeOfVisaLabel() {
    return this.state.entryOptionLabels.hasOwnProperty(this.model.entries)
      ? this.state.entryOptionLabels[this.model.entries]
      : this.toTitleCase(this.model.entries);
  }

  /**
   * Convert the string to Title Case
   * @param str
   */
  toTitleCase(str) {
    return str.toLowerCase()
      .split(' ')
      .map(_.capitalize)
      .join(' ');
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/common/product-options/product-options.component.ts